// Khoá TanStack Query theo thực thể — invalidate theo tiền tố (VD ['accounts'] làm mới mọi danh sách tài khoản).
// Nhóm mới theo FSD.md §2.4. Khoá nhân sự ('hr') hiện khai báo trong entities/employee, entities/team (D13).
import type { QueryClient } from '@tanstack/react-query';

export const qk = {
  viewer: {
    all: ['viewer'] as const,
    bootstrap: () => ['viewer', 'bootstrap'] as const,
    permissions: () => ['viewer', 'permissions'] as const,
    sessions: () => ['viewer', 'sessions'] as const,
    joinRequest: () => ['viewer', 'join-request'] as const,
  },
  tenant: {
    settings: () => ['tenant', 'settings'] as const,
    modules: () => ['tenant', 'modules'] as const,
    branding: (slug: string) => ['tenant', 'branding', slug] as const,
  },
  accounts: {
    all: ['accounts'] as const,
    list: (f: object) => ['accounts', 'list', f] as const,
    detail: (staffId: string) => ['accounts', 'detail', staffId] as const,
    permissions: (staffId: string) => ['accounts', 'permissions', staffId] as const,
    eligible: (perm: string) => ['accounts', 'eligible', perm] as const,
  },
  roles: {
    all: ['roles'] as const,
    list: (detail: boolean) => ['roles', 'list', detail] as const,
    detail: (id: string) => ['roles', 'detail', id] as const,
  },
  permissions: { catalog: () => ['permissions', 'catalog'] as const },
  joinRequests: { all: ['join-requests'] as const, list: (status: string) => ['join-requests', status] as const },
  users: { search: (q: string) => ['users', 'search', q] as const },
  /** Hộp "Việc cần xử lý" (chuông, số trên menu, thẻ Tổng quan). */
  inbox: { all: ['inbox'] as const },
  /** Thông báo cá nhân (tab "Thông báo" trong menu chuông) — notification-service ListNotifications / unread-count. */
  notifications: {
    all: ['notifications'] as const,
    list: (f: object) => ['notifications', 'list', f] as const,
    unread: () => ['notifications', 'unread'] as const,
  },
  /** Kết quả Tìm nhanh theo nguồn (SearchProvider.id). */
  search: { all: ['search'] as const, q: (provider: string, q: string) => ['search', provider, q] as const },
  dashboard: { all: ['dashboard'] as const, view: (month: string) => ['dashboard', month] as const },
  process: { all: ['process'] as const, summary: () => ['process', 'summary'] as const },
  customers: {
    all: ['customers'] as const,
    list: (f: object) => ['customers', 'list', f] as const,
    facets: (f: object) => ['customers', 'facets', f] as const,
    detail: (id: string) => ['customers', 'detail', id] as const,
    activities: (id: string) => ['customers', 'activities', id] as const,
    matches: (id: string, o: object) => ['customers', 'matches', id, o] as const,
    tasks: (id: string) => ['customers', 'tasks', id] as const,
    options: (purpose: string, q: string) => ['customers', 'options', purpose, q] as const,
    phoneCheck: (phone: string, exceptId?: string) => ['customers', 'phone-check', phone, exceptId ?? ''] as const,
    assignees: (purpose: string, o: object) => ['customers', 'assignees', purpose, o] as const,
    unitTypes: () => ['customers', 'unit-types'] as const,
    import: (id: string) => ['customers', 'import', id] as const,
    export: (id: string) => ['customers', 'export', id] as const,
  },
  tasks: {
    all: ['tasks'] as const,
    list: (f: object) => ['tasks', 'list', f] as const,
    counts: (staffId: string) => ['tasks', 'counts', staffId] as const,
    detail: (id: string) => ['tasks', 'detail', id] as const,
  },
  inventory: {
    all: ['inventory'] as const,
    projects: () => ['inventory', 'projects'] as const,
    project: (id: string) => ['inventory', 'project', id] as const,
    units: (projectId: string, f: object) => ['inventory', 'units', projectId, f] as const,
    unit: (id: string) => ['inventory', 'unit', id] as const,
    owner: (id: string) => ['inventory', 'owner', id] as const,
    priceHistory: (id: string) => ['inventory', 'price-history', id] as const,
    quote: (id: string, pct: number) => ['inventory', 'quote', id, pct] as const,
    unitTypes: (projectId?: string) => ['inventory', 'unit-types', projectId ?? ''] as const,
    overview: () => ['inventory', 'overview'] as const,
    deletion: (id: string) => ['inventory', 'deletion', id] as const,
    genPreview: (projectId: string, input: object) => ['inventory', 'gen-preview', projectId, input] as const,
    importPreview: (projectId: string, text: string) => ['inventory', 'import-preview', projectId, text] as const,
  },
  booking: {
    all: ['booking'] as const,
    projects: () => ['booking', 'projects'] as const,
    board: (projectId: string) => ['booking', 'board', projectId] as const,
    unit: (unitId: string) => ['booking', 'unit', unitId] as const,
    myLocks: () => ['booking', 'my-locks'] as const,
    activity: (projectId: string) => ['booking', 'activity', projectId] as const,
    summary: () => ['booking', 'summary'] as const,
    matches: (customerId: string, projectId: string) => ['booking', 'matches', customerId, projectId] as const,
  },
  /** Nội dung Trình chiếu dự án — ngoài 'booking' để tin booking.units (đổi trạng thái căn) không tải lại ảnh / mặt bằng. */
  showroom: {
    all: ['showroom'] as const,
    project: (projectId: string) => ['showroom', projectId] as const,
  },
  deals: {
    all: ['deals'] as const,
    list: (f: object) => ['deals', 'list', f] as const,
    overview: (month: string) => ['deals', 'overview', month] as const,
    detail: (id: string) => ['deals', 'detail', id] as const,
    commission: (id: string) => ['deals', 'commission', id] as const,
    submission: (id: string) => ['deals', 'submission', id] as const,
    announcement: (id: string) => ['deals', 'announcement', id] as const,
    unitChangeOptions: (id: string) => ['deals', 'unit-change-options', id] as const,
    unitChange: (sagaId: string) => ['deals', 'unit-change', sagaId] as const,
    /** GET /crm/customers/:id/deals — đặt dưới 'deals' để mọi thao tác giao dịch làm mới hồ sơ khách. */
    byCustomer: (customerId: string) => ['deals', 'by-customer', customerId] as const,
  },
  attendance: {
    all: ['attendance'] as const,
    policy: () => ['attendance', 'policy'] as const,
    today: () => ['attendance', 'today'] as const,
    myTimesheet: (m: string) => ['attendance', 'my-timesheet', m] as const,
    teamTimesheet: (m: string, f: object) => ['attendance', 'team-timesheet', m, f] as const,
    period: (m: string) => ['attendance', 'period', m] as const,
    day: (staffId: string, date: string) => ['attendance', 'day', staffId, date] as const,
    leaves: (status: string) => ['attendance', 'leaves', status] as const,
    pendingCount: () => ['attendance', 'leaves', 'pending-count'] as const,
    balance: (year: number) => ['attendance', 'leave-balance', year] as const,
    presence: (scope: string) => ['attendance', 'presence', scope] as const,
  },
  sales: {
    all: ['sales'] as const,
    report: (m: string) => ['sales', 'report', m] as const,
    targets: (m: string) => ['sales', 'targets', m] as const,
  },
  commission: {
    all: ['commission'] as const,
    me: () => ['commission', 'me'] as const,
    summary: () => ['commission', 'summary'] as const,
    ledger: (f: object) => ['commission', 'ledger', f] as const,
    payout: (period: string) => ['commission', 'payout', period] as const,
    payslip: (period: string, staffId: string) => ['commission', 'payslip', period, staffId] as const,
    policy: () => ['commission', 'policy'] as const,
    carryForwards: (f: object) => ['commission', 'carry-forwards', f] as const,
  },
};

export type AnyQueryKey = readonly unknown[];

/**
 * Làm mới lại các tiền tố sau một nhịp (mặc định 1,5 s) — đón phần cập nhật bất đồng bộ qua sự kiện (projection)
 * sau khi đã invalidate ngay. Trả hàm huỷ.
 */
export function invalidateSoon(qc: QueryClient, keys: readonly AnyQueryKey[], ms = 1500) {
  const t = setTimeout(() => {
    for (const queryKey of keys) void qc.invalidateQueries({ queryKey });
  }, ms);
  return () => clearTimeout(t);
}
